import { FC, PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { useNavigation } from '../context/navigation-context-provider';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { cx } from '@emotion/css';
import { useBreakpoints } from '@phoenix/design-system/frontend/ui/breakpoints/use-breakpoints';
import { GlobalLink, Section } from '../../../domain/static-content/static-content.static-generation.model';
import { useStihlIdNavigation } from '../hooks/useStihlIdNavigation';
import { AppSwitcher } from './app-switcher';
import { MobileMenu } from './mobile-menu';
import { getLocale } from '../../../util/locales/locales';
import Skeleton from 'react-loading-skeleton';
import { getDesignSystemIconWithFallback, getInitials } from '../helpers/utils';

export const MobileNavMainMenu = () => {
  const {
    config,
    state: { showMobileSubMenu },
  } = useNavigation();
  const { t } = useTranslation();

  return (
    (
      <>
        {config.mainNavItems
          ?.filter((item) => item.type === 'Section')
          ?.map((item) => (
            <MobileMenu
              key={item.name}
              condition={showMobileSubMenu === item.id}
              ariaLabelNav={t('shared:shared_globalNav_mobile_aria_label')}
            >
              <MobileMenu.List>
                <MobileMenu.SectionHeader label={item.name} />
                {item.children?.map((subItem) => (
                  <MobileMenu.Item key={subItem.id} item={subItem} isToplevelItem={false} />
                ))}
              </MobileMenu.List>
            </MobileMenu>
          ))}
      </>
    ) || null
  );
};

interface MobileNavFooterSubMenuProps {
  additionalSubMenuContentItems?: Section[];
}

export const MobileNavFooterSubMenu: FC<MobileNavFooterSubMenuProps> = ({ additionalSubMenuContentItems }) => {
  const {
    state: { showMobileSubMenu },
  } = useNavigation();
  const { t } = useTranslation();
  return (
    (
      <>
        {additionalSubMenuContentItems?.map((item) => (
          <MobileMenu
            key={item.id}
            condition={showMobileSubMenu === item.id}
            ariaLabelNav={t('shared:shared_globalNavFooter_mobile_aria_label')}
          >
            <MobileMenu.List>
              <MobileMenu.SectionHeader label={item.title} />
              {item.links?.map((subItem) => (
                <MobileMenu.Item key={subItem.id} item={subItem} isToplevelItem={false} />
              ))}
            </MobileMenu.List>
          </MobileMenu>
        ))}
      </>
    ) || null
  );
};

interface MobileNavSubMenuProps {
  additionalSubMenuContentItems?: Section[];
}

export const MobileNavSubMenu: FC<PropsWithChildren<MobileNavSubMenuProps>> = ({ children, additionalSubMenuContentItems }) => {
  const {
    auth,
    state: { showMobileSubMenu },
    setState,
    config,
  } = useNavigation();
  const { t } = useTranslation();
  const useStihlNavigation = useStihlIdNavigation(auth?.isAuthenticated, config?.locale);

  return (
    <>
      <div className="relative z-10 flex flex-col overflow-auto py-4">
        {/* START ~ Regular Menu*/}
        <MobileMenu.List>
          {config.mainNavItems?.map((item) => (
            <MobileMenu.Item key={item.id} item={item} preventFocus={showMobileSubMenu} isToplevelItem={true} />
          ))}
        </MobileMenu.List>
        {/* END ~ Regular Menu*/}

        {/* START ~ STIHL ID Menu*/}
        <MobileMenu.List className={cx('mx-4', config.mainNavItems && 'mt-6 border-t border-secondary-mid pt-6')}>
          {auth?.isAuthenticated ? (
            <MobileMenu.Item
              as="button"
              className={cx('flex w-full items-center justify-between py-4')}
              onClick={() => {
                setState((prevState) => ({ ...prevState, showMobileSubMenu: 'stihl-id-menu' }));
              }}
              disabled={!auth?.displayName}
              preventFocus={showMobileSubMenu}
              isToplevelItem={true}
            >
              {auth?.displayName ? (
                <span className="flex items-center gap-4">
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-deep text-primary-white !text-xs stihl-text-interactive-text"
                    data-testid="mobilenavigation_stihlid_userInitials"
                  >
                    {getInitials(auth?.displayName)}
                  </span>
                  <span className="stihl-text-body-default-reg">{auth?.displayName}</span>
                </span>
              ) : (
                <div className="flex items-center gap-4">
                  <Skeleton circle={true} className="h-6 w-6" />
                  <Skeleton className="h-4 w-52" />
                </div>
              )}
              <img src={getDesignSystemIconWithFallback('icon-chevron-right.svg')} alt="Icon Right" className="h-4 w-4" />
            </MobileMenu.Item>
          ) : (
            <MobileMenu.Item
              as="a"
              href={auth?.postLoginRedirectUrl?.replace('{locale}', getLocale().RFC5646?.toLowerCase())}
              className={cx('flex w-full items-center justify-between py-4')}
              target="_self"
              data-testid="mobilenavigation_stihlid_login"
            >
              <span className="flex items-center gap-4">
                <svg className="block h-6 w-6 fill-current text-primary-black">
                  <use xlinkHref={getDesignSystemIconWithFallback('icon-user.svg') + '#img'} />
                </svg>
                <span className="stihl-text-body-default-reg">{t('shared:topBar_meta_login_label')}</span>
              </span>
              <img src={getDesignSystemIconWithFallback('icon-chevron-right.svg')} alt="Icon Right" className="h-4 w-4" />
            </MobileMenu.Item>
          )}
          <MobileMenu.Item
            as="button"
            className={cx('flex w-full items-center justify-between py-4')}
            data-testid="mobilenavigation_stihlid_appswitcher_button"
            preventFocus={showMobileSubMenu}
            onClick={() => {
              setState((prevState) => ({ ...prevState, showMobileSubMenu: 'app-switcher' }));
            }}
          >
            <span className="flex items-center gap-4">
              <span className="flex h-6 w-6 items-center justify-center">
                <img src={getDesignSystemIconWithFallback('icon-squares-grid.svg')} alt="" className="h-4 w-4 fill-current" />
              </span>
              <span className="stihl-text-body-default-reg">{t('shared:topBar_meta_apps_label')}</span>
            </span>
            <img src={getDesignSystemIconWithFallback('icon-chevron-right.svg')} alt="Icon Right" className="h-4 w-4" />
          </MobileMenu.Item>
        </MobileMenu.List>
        {/* END ~ STIHL ID Menu*/}
      </div>

      {((config.metaNavItems && config.metaNavItems?.length > 0) || auth?.isAuthenticated) && (
        <nav className="relative z-10 bg-secondary-light py-4" aria-label={t('shared:shared_globalNav_mobile_aria_label')}>
          <MobileMenu.List>
            {config.metaNavItems?.map((item, idx) => (
              <MobileMenu.Item
                key={`${item.id}-${idx}`}
                item={item}
                className={cx('flex w-full items-center justify-between px-4 py-3 stihl-text-body-sm-reg')}
              >
                {'component' in item ? (
                  item.component
                ) : (
                  <>
                    <span className="flex items-center gap-2">
                      {item.icon && <img src={item.icon} alt="Icon" className="h-4 w-4" />}
                      <span>{item.name}</span>
                    </span>
                    <img src={getDesignSystemIconWithFallback('icon-chevron-right.svg')} alt="Icon Right" className="h-4 w-4" />
                  </>
                )}
              </MobileMenu.Item>
            ))}
            {children}
            {auth?.isAuthenticated && (
              <MobileMenu.Item
                as="button"
                onClick={auth?.onLogout}
                className="flex w-full items-center justify-between px-4 py-3 stihl-text-body-sm-reg"
                data-testid="mobilenavigation_stihlid_logout"
                preventFocus={showMobileSubMenu}
              >
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 fill-current">
                    <use xlinkHref={getDesignSystemIconWithFallback('icon-exit-16.svg') + '#img'} />
                  </svg>
                  <span>{t('shared:topBar_meta_userMenu_logout')}</span>
                </span>
              </MobileMenu.Item>
            )}
          </MobileMenu.List>
        </nav>
      )}

      <MobileMenu condition={showMobileSubMenu === 'stihl-id-menu'} ariaLabelNav={t('shared:shared_globalNavStihlId_mobile_aria_label')}>
        <MobileMenu.List>
          {auth?.displayName && (
            <MobileMenu.SectionHeader className="justify-between">
              <span className="stihl-text-body-lg-bold">{auth?.displayName}</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-deep text-primary-white !text-xs stihl-text-interactive-text">
                {getInitials(auth?.displayName)}
              </span>
            </MobileMenu.SectionHeader>
          )}
          {useStihlNavigation.stihlIdNavigationItemsQuery.data
            ?.filter((item) => item.url)
            .map((item: GlobalLink) => (
              <MobileMenu.Item key={item.id} className={cx('first:pt-4 last:pb-4')} item={item}>
                {item.name}
              </MobileMenu.Item>
            ))}
        </MobileMenu.List>
      </MobileMenu>

      <MobileMenu condition={showMobileSubMenu === 'app-switcher'} ariaLabelNav={t('shared:shared_globalNav_mobile_aria_label')}>
        <AppSwitcher />
      </MobileMenu>

      <MobileNavFooterSubMenu additionalSubMenuContentItems={additionalSubMenuContentItems} />
    </>
  );
};

interface MobileNavigationProps {
  additionalSubMenuContent?: ReactNode;
  additionalSubMenuContentItems?: Section[];
}

export const MobileNavigation: FC<MobileNavigationProps> = ({ additionalSubMenuContent, additionalSubMenuContentItems }) => {
  const {
    state: { showMobileMenu },
    config: { shouldHideMainNavOnDesktop },
    setState,
  } = useNavigation();
  const { tablet, desktop } = useBreakpoints();
  const originalStyleRef = useRef<null | string>(null);

  useEffect(() => {
    originalStyleRef.current = document.body.style.overflow;
  }, []);

  useEffect(() => {
    const reachedBreakpointToHideMobileMenuAt = shouldHideMainNavOnDesktop ? desktop : tablet;
    if (reachedBreakpointToHideMobileMenuAt && showMobileMenu) {
      setState((state) => ({ ...state, showMobileMenu: false }));
    }
  }, [shouldHideMainNavOnDesktop, desktop, tablet]);

  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      if (originalStyleRef.current !== null && originalStyleRef.current !== document.body.style.overflow) {
        document.body.style.overflow = originalStyleRef.current;
      }
    }
  }, [showMobileMenu]);

  return (
    <div
      className={cx('absolute left-0 right-0 top-0 z-50', shouldHideMainNavOnDesktop ? 'desktop:hidden' : 'tablet:hidden')}
      data-testid="navigation_mobile_navigation"
    >
      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 top-[3.125rem] z-10 bg-primary-white"
          >
            <div className="absolute inset-0 flex flex-col justify-between">
              <MobileNavMainMenu />
              <MobileNavSubMenu additionalSubMenuContentItems={additionalSubMenuContentItems}>{additionalSubMenuContent}</MobileNavSubMenu>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
